import { makeAutoObservable, runInAction } from 'mobx'
import type { ReactNode } from 'react'
import { nanoid } from 'nanoid'
import { type Blocker } from 'react-router'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type IParamsUpdateInboxAiAgent, type IResponseInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox'
import { AiRepliesStore } from 'widgets/AiReplies'

export class MessagingAiAutomationStore {
  private _modalId = nanoid()
  private _teamsId: number | null = null
  private _showAiReplies = false
  private _aiRepliesSending = false
  private _aiRepliesStore: AiRepliesStore | null = null
  private _savedChatbotId: number | null = null

  constructor() {
    makeAutoObservable(this)
  }

  init = async (inbox: IResponseInbox) => {
    try {
      this._teamsId = inbox.id
      this._aiRepliesStore = new AiRepliesStore({
        clearError: () => {
          this.setAiRepliesError('')
        },
      })

      const replyAutomationOptionsId = inbox?.reply_automation?.options.id

      if (replyAutomationOptionsId) {
        this._aiRepliesStore.isAiRepliesActive = true
        await this._aiRepliesStore.loadDataByChatbotId(replyAutomationOptionsId)

        runInAction(() => {
          const chatbotId = this._aiRepliesStore?.chatbot_id ?? null

          this._showAiReplies = Boolean(chatbotId)
          this._savedChatbotId = chatbotId
        })
      } else {
        runInAction(() => {
          this._showAiReplies = false
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setAiRepliesError = (error: ReactNode) => {
    this._aiRepliesStore?.setError(error)
  }

  toggleShowAiReplies = (value: boolean) => {
    this._showAiReplies = value

    if (!value) this._aiRepliesStore?.setAiRepliesState(false)
    if (!value && this._aiRepliesStore?.chatbot_id !== this._savedChatbotId) {
      void this.sendAiReplay()
    }
  }

  handleShowKeepEditingModal = (blocker: Blocker) => {
    if (blocker.state !== 'blocked') return

    modalStore.addModal({
      id: this._modalId,
      title: 'Incomplete setup',
      desc: [
        'You’ve enabled AI Automation, but no AI Agent is selected. If you leave now, your automation won’t be saved.',
      ],
      type: ModalTypeList.INFO,
      primaryAction: {
        text: 'Select AI agent',
        onAction: () => modalStore.closeModal(this._modalId),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          const path = blocker.location.pathname

          this._showAiReplies = false
          blocker.proceed()
          uiStore.setRoutesBlocker(null)
          uiStore.changeRoute({
            path,
            type: uiStore.checkRouteType(blocker.location.pathname),
            method: 'replace',
          })
          modalStore.closeModal(this._modalId)
        },
      },
    })
  }

  sendAiReplay = async () => {
    try {
      if (
        !this._aiRepliesStore ||
        !this._teamsId ||
        !this.aiRepliesParams ||
        this._aiRepliesSending
      )
        return

      runInAction(() => {
        this._aiRepliesSending = true
      })

      await InboxesApi.updateInboxAiAgent(this._teamsId, this.aiRepliesParams)

      runInAction(() => {
        this._savedChatbotId = this.aiRepliesParams.reply_automation?.options.id ?? null
      })

      toastStore.add({
        type: 'success',
        title: 'AI automation saved!',
      })
    } catch (e) {
      logger.error(e)

      toastStore.add({
        type: 'error',
        title: 'Something went wrong, try again!',
      })
    } finally {
      this._aiRepliesSending = false
    }
  }

  get showAiReplies() {
    return this._showAiReplies
  }

  get aiRepliesParams() {
    const params: IParamsUpdateInboxAiAgent = {
      reply_automation: null,
    }

    if (this._aiRepliesStore?.chatbot_id) {
      params.reply_automation = {
        type: 'chatbot',
        options: {
          id: this._aiRepliesStore.chatbot_id,
        },
      }
    }

    return params
  }

  get aiRepliesSending() {
    return this._aiRepliesSending
  }

  get hasUnsavedChanges() {
    return this.showAiReplies && !this._aiRepliesStore?.chatbot_id
  }

  get aiRepliesStore() {
    return this._aiRepliesStore
  }

  get savedChatbotId() {
    return this._savedChatbotId
  }
}
